import { Description } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, Stack, TableCell, TableRow, TextField, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import BayerTable, { type BayerTableColumn } from '/@/components/bayer/table';
import { TableTemplateCell } from '/@/components/bayer/table/template-cell';
import { useUnitProvider } from '/@/core/context/unit';
import { useCropMonitorings } from '/@/hooks/crop-monitoring/use-crop-monitorings';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useFieldById } from '/@/hooks/fields';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useHasWriteAccess } from '/@/hooks/session';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import type { CropMonitoring, CropType } from '/@/types/crop-monitoring';
import { formatDate } from '/@/utils';
import type { NodeScopeFilters } from '/@/utils/node-scope';
import { reverse } from '/@/utils/router';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import CreateReportModal from '../../../components/create-report-modal';
import CropMonitoringMoreOptions from '../../../components/more-options';

const PageReportsRow = ({
    cropMonitoring,
    setSelectedCropMonitoring,
}: {
    cropMonitoring: CropMonitoring;
    setSelectedCropMonitoring: (row: CropMonitoring | null) => void;
}) => {
    const navigate = useNavigate();
    const units = useUnitProvider();

    const crops = getCrops();
    const canEditResource = useHasWriteAccess();

    return (
        <TableRow
            key={cropMonitoring.id}
            sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => {
                if (cropMonitoring.result?.id) {
                    navigate(
                        reverse('cropMonitoring.CornCropMonitoringDetail.report', {
                            params: {
                                cropMonitoringId: cropMonitoring?.id,
                            },
                        })
                    );
                }
            }}
        >
            <TableCell width='18%'>
                <TableTemplateCell title={cropMonitoring.fieldName} />
            </TableCell>
            <TableCell width='18%'>
                <TableTemplateCell
                    title={
                        cropMonitoring.campaign
                            ? `${cropMonitoring.campaign}/${Number(cropMonitoring.campaign) + 1}`
                            : ''
                    }
                />
            </TableCell>
            <TableCell width='18%'>
                <Typography variant='body2' color='primary'>
                    {crops[cropMonitoring.previousCrop as keyof typeof crops]?.getChip({
                        text: '',
                    })}
                </Typography>
            </TableCell>
            <TableCell width='18%'>
                <Typography variant='body2' color='primary'>
                    {crops[cropMonitoring.crop as keyof typeof crops]?.getChip({
                        text: cropMonitoring.productName || '',
                    })}
                </Typography>
            </TableCell>
            <TableCell width='18%'>
                <TableTemplateCell title={formatDate(new Date(cropMonitoring.plantingDate))} />
            </TableCell>
            {!cropMonitoring.result ? (
                <TableCell width='36%' colSpan={2}>
                    <TableTemplateCell
                        title={_('Sin reporte generado')}
                        subtitleButton={canEditResource ? _('Generar reporte') : undefined}
                        subtitleButtonClick={(e) => {
                            if (!canEditResource) return;
                            e.stopPropagation();
                            setSelectedCropMonitoring(cropMonitoring);
                        }}
                    />
                </TableCell>
            ) : (
                <>
                    <TableCell width='18%'>
                        {cropMonitoring.harvestDate ? (
                            <TableTemplateCell title={formatDate(new Date(cropMonitoring.harvestDate))} />
                        ) : (
                            <TableTemplateCell
                                title={_('Sin reporte generado')}
                                subtitleButton={canEditResource ? _('Generar reporte') : undefined}
                                subtitleButtonClick={(e) => {
                                    if (!canEditResource) return;
                                    e.stopPropagation();
                                    setSelectedCropMonitoring(cropMonitoring);
                                }}
                            />
                        )}
                    </TableCell>
                    <TableCell width='18%'>
                        <TableTemplateCell
                            title={units
                                .toYield(cropMonitoring.result?.averageYield || 0, cropMonitoring.crop, 2)
                                .toString()}
                        />
                    </TableCell>
                </>
            )}
            <TableCell>
                <CropMonitoringMoreOptions
                    fieldId={cropMonitoring.field}
                    cropMonitoring={cropMonitoring}
                    disableOptions={['archive', 'edit']}
                />
            </TableCell>
        </TableRow>
    );
};

const FieldMonitoringDashboardPastReports = ({
    growerNodeId,
    year,
    nodeScope,
}: {
    growerNodeId: number;
    year: number;
    nodeScope: NodeScopeFilters;
}) => {
    const [search, setSearch] = useState('');
    const [cropFilter, setCropFilter] = useState('');
    const debouncedSearch = useDebounce(search, 1000);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // order by
    const [orderBy, setOrderBy] = useState('plantingDate');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');

    // Map frontend column names to backend field names
    const orderingFieldMap: Record<string, string> = {
        plantingDate: 'planting_date',
        harvestDate: 'harvest_date',
    };

    // Compute ordering string for API
    const ordering = useMemo(() => {
        const fieldName = orderingFieldMap[orderBy] || orderBy;
        return order === 'desc' ? `-${fieldName}` : fieldName;
    }, [orderBy, order]);

    const cropMonitoringsListQuery = useCropMonitorings({
        page,
        pageSize,
        crop: cropFilter !== '' ? (cropFilter as CropType) : undefined,
        productName: debouncedSearch,
        active: false,
        ordering,
        node: growerNodeId,
        campaign: year,
        ...nodeScope,
    });

    const cropMonitorings = useMemo(() => {
        return cropMonitoringsListQuery.data?.results || [];
    }, [cropMonitoringsListQuery.data]);
    const ownUserNode = useOwnUserNode();

    const [selectedCropMonitoring, setSelectedCropMonitoring] = useState<CropMonitoring | null>(null);
    const fieldQuery = useFieldById(
        { id: selectedCropMonitoring?.field },
        { enabled: !!selectedCropMonitoring?.field }
    );
    // Create Report Modal
    const [createReportModalIsOpen, setCreateReportModalIsOpen] = useState(false);

    const crops = Object.values(getCrops())?.filter((crop) => crop.value !== 'fallow') || []; // Can't have a report of type fallow
    const columns: BayerTableColumn[] = [
        { label: _('Lote'), value: 'field' },
        { label: _('Campaña'), value: 'campaign' },
        { label: _('Cultivo antecesor'), value: 'previousCrop' },
        { label: _('Cultivo'), value: 'name' },
        {
            label: _('Fecha de siembra'),
            value: 'plantingDate',
            sortable: true,
            sortKey: 'plantingDate',
            sortOrder: 'asc',
        },
        {
            label: _('Fecha de cosecha'),
            value: 'harvestDate',
            sortable: true,
            sortKey: 'harvestDate',
            sortOrder: 'asc',
        },
        { label: _('Rendimiento promedio'), value: 'averageYield' },
        { label: '', value: 'actions' },
    ];

    // Empty State
    const emptyState = useMemo(() => {
        const isFiltering = search || cropFilter;
        let emptyIcon: React.ReactNode;
        let emptyTitle = '';
        let emptySubtitle = '';
        let emptyButton: React.ReactNode;

        emptyIcon = <Description color='primary' sx={{ width: 32, height: 32 }} />;
        emptyTitle = _('Sin reportes de campaña disponibles');
        emptySubtitle = _(
            'Cuando los productores que agregaste finalicen sus campañas, vas a poder ver acá un resumen de sus resultados.'
        );
        const isOwn = ownUserNode.data?.id === growerNodeId;
        if (isOwn) {
            emptySubtitle = _('Al finalizar las campañas, vas a poder ver acá un resumen de tus resultados.');
        }

        if (isFiltering) {
            emptyTitle = _('No se encontraron resultados');
            emptySubtitle = '';
        }

        return {
            title: emptyTitle,
            subtitle: emptySubtitle,
            icon: emptyIcon,
            button: emptyButton,
        };
    }, [search, cropFilter]);

    return (
        <Stack direction='column' spacing={3}>
            <Grid2 container spacing={2} fullWidth>
                <Grid2 xs={12} md={8}>
                    <TextField
                        label={_('Buscar un reporte por producto')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        fullWidth
                    />
                </Grid2>
                <Grid2 xs={12} md={4}>
                    <MuiSelect
                        inputLabel={_('Tipo')}
                        value={cropFilter}
                        onChange={(e) => setCropFilter(e.target.value as string)}
                        options={
                            crops.map((crop) => {
                                return {
                                    value: crop.value,
                                    label: crop.name,
                                };
                            }) || []
                        }
                        clearable
                        formControlProps={{
                            fullWidth: true,
                        }}
                    />
                </Grid2>
            </Grid2>
            <BayerTable
                columns={columns}
                rows={cropMonitorings.map((cropMonitoring) => (
                    <PageReportsRow
                        key={cropMonitoring.id}
                        cropMonitoring={cropMonitoring}
                        setSelectedCropMonitoring={(row) => {
                            setSelectedCropMonitoring(row);
                            setCreateReportModalIsOpen(true);
                        }}
                    />
                ))}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                orderBy={orderBy}
                order={order}
                setOrderBy={setOrderBy}
                setOrder={setOrder}
                isValidating={cropMonitoringsListQuery.isLoading}
                count={cropMonitoringsListQuery.data?.count}
                emptyState={emptyState}
            />
            {selectedCropMonitoring && fieldQuery.data && (
                <CreateReportModal
                    field={fieldQuery.data}
                    cropMonitoring={
                        cropMonitorings.find((cropMonitoring) => cropMonitoring.id === selectedCropMonitoring?.id) ??
                        selectedCropMonitoring
                    }
                    isOpen={createReportModalIsOpen}
                    setIsOpen={setCreateReportModalIsOpen}
                />
            )}
        </Stack>
    );
};

export default FieldMonitoringDashboardPastReports;
